import { EventEmitter } from 'eventemitter3';
import { cloneDeep, get, set } from 'lodash';

import {
  BaseResource,
  StoredNode,
  TreeBrowserEvent,
  TreeBrowserEvents,
  TreeBrowserLoaderOptions,
  TreeBrowserNode,
  TreeBrowserNodeMeta,
  TreeBrowserOptions,
} from './types';

interface FlattenTreeOptions {
  dropCollapsedNodes?: boolean;
  injectLoadMoreRow?: boolean;
}

export const propertyMapper = <T extends object, U>(key: string) => {
  return (node: T, value?: U) => {
    if (typeof value !== 'undefined') {
      return set(cloneDeep(node), key, value);
    }
    return get(node, key);
  };
};

const MAX_NUM_CHILDREN = 1_000;
const DEFAULT_PAGE_SIZE = 20;
const MAX_EXPAND_DEPTH = 20;

/*
 * This class facilitates progressively loading a tree structure of resources.
 *
 * Nodes are stored in an unordered Map keyed by ID. The ordered flat list
 * (with computed depths) is derived on-demand from parent-child relationships.
 * This simplifies operations like moving nodes - just update the parentNodeId.
 */
export class TreeBrowser<
  T extends BaseResource,
  U = T['id'],
> extends EventEmitter<TreeBrowserEvents> {
  private nodesById: Map<U, StoredNode<T>> = new Map();
  private nodesByParentId: Map<U, StoredNode<T>[]> = new Map();
  private rootNodeId: U;
  private isBatching = 0;
  private cachedTree: TreeBrowserNode<T>[] | null = null;
  private cachedAllNodes: TreeBrowserNode<T>[] | null = null;
  private nodeLoader: TreeBrowserOptions<T, U>['nodeLoader'];
  public pageSize: number = DEFAULT_PAGE_SIZE;

  private getParentId: (node: StoredNode<T>) => U;
  private setParentId: (node: StoredNode<T>, parentId: U) => StoredNode<T>;
  private getHasChildren: (node: StoredNode<T>) => boolean;
  private setHasChildren: (
    node: StoredNode<T>,
    hasChildren: boolean,
  ) => StoredNode<T>;

  constructor({
    rootNodeId,
    parentIdMapper,
    hasChildrenMapper,
    nodeLoader,
    pageSize = DEFAULT_PAGE_SIZE,
  }: TreeBrowserOptions<T, U>) {
    super();
    this.rootNodeId = rootNodeId;
    this.nodeLoader = nodeLoader;
    this.pageSize = pageSize;

    this.getParentId = (node) => parentIdMapper(node) as U;
    this.setParentId = (node, value) =>
      parentIdMapper(node, value) as StoredNode<T>;
    this.setHasChildren = (node, value) =>
      hasChildrenMapper(node, value) as StoredNode<T>;
    this.getHasChildren = (node) => hasChildrenMapper(node) as boolean;

    this.loadRootNode(rootNodeId);
  }

  private generateNodesByParentId = () => {
    this.nodesByParentId.clear();

    this.nodesById.forEach((node) => {
      const parentId = this.getParentId(node);
      if (parentId) {
        const siblings = this.nodesByParentId.get(parentId) || [];

        this.nodesByParentId.set(parentId, [...siblings, node]);
      }
    });
  };

  private emitChange = () => {
    if (this.isBatching) {
      return;
    }

    this.cachedTree = null;
    this.cachedAllNodes = null;
    this.emit(TreeBrowserEvent.CHANGE);
  };

  private patchNode = (id: U, node: Partial<StoredNode<T>> | T) => {
    const existingNode = this.nodesById.get(id) ?? { meta: {} };
    const meta = { ...existingNode.meta, ...('meta' in node ? node.meta : {}) };
    const mergedNode = {
      ...existingNode,
      ...node,
      meta,
    } as StoredNode<T>;

    this.nodesById.set(id, mergedNode);

    this.emitChange();
  };

  private batchChanges = async (fn: () => Promise<void> | void) => {
    this.isBatching++;
    await fn();
    this.isBatching--;
    this.emitChange();
  };

  private patchMeta = (id: U, meta: Partial<TreeBrowserNodeMeta>) => {
    return this.patchNode(id, { meta } as Partial<StoredNode<T>>);
  };

  private generateTree = ({
    dropCollapsedNodes,
    injectLoadMoreRow,
  }: FlattenTreeOptions = {}) => {
    const result: TreeBrowserNode<T>[] = [];

    const traverse = (parentId: U, depth: number) => {
      const children = this.nodesByParentId.get(parentId) || [];

      for (const child of children) {
        result.push({
          ...child,
          meta: { ...child.meta, depth },
        });

        if (child.meta.isExpanded || !dropCollapsedNodes) {
          traverse(child.id as U, depth + 1);
        }
      }

      // Inject a dummy node to indicate that there are more children to load. The UI can use this special node
      // to render a load more button.
      if (injectLoadMoreRow && this.nodesById.get(parentId)?.meta.hasMore) {
        result.push({
          id: parentId + '-load-more',
          meta: { depth, isLoadMoreNode: true },
          attributes: {
            parentNodeId: parentId,
          },
        } as unknown as TreeBrowserNode<T>);
      }
    };

    traverse(this.rootNodeId, 1);

    return result;
  };

  setPageSize = (pageSize: number) => {
    this.pageSize = pageSize;
  };

  reload = () => {
    this.nodesById.clear();
    this.nodesByParentId.clear();
    return this.loadRootNode(this.rootNodeId);
  };

  loadRootNode = async (rootNodeId: U) => {
    await this.loadChildren(rootNodeId);

    this.emit(TreeBrowserEvent.INITIAL_LOAD);
  };

  expand = async (id: U) => {
    const node = this.nodesById.get(id);

    if (!node?.meta.hasLoaded) {
      await this.loadChildren(id);
    }

    this.patchMeta(id, { isExpanded: true });
  };

  collapse = (id: U) => {
    this.patchMeta(id, { isExpanded: false });
  };

  loadChildren = async (
    id: U | undefined | null,
    options?: TreeBrowserLoaderOptions,
  ) => {
    if (!id) {
      return;
    }

    this.patchMeta(id, { isLoading: true });

    const {
      items: children,
      hasMore,
      total,
    } = await this.nodeLoader(id, {
      limit: this.pageSize,
      ...options,
    });

    await this.batchChanges(async () => {
      this.patchMeta(id, { hasMore, total });

      children.forEach?.((child) => {
        this.patchNode(child.id as U, child);
      });

      this.patchMeta(id, {
        isLoading: false,
        hasLoaded: true,
      });
    });

    return { children, hasMore };
  };

  loadMore = async (id: U) => {
    const numChildren = this.nodesByParentId.get(id)?.length ?? 0;

    return this.loadChildren(id, { offset: numChildren });
  };

  getNodeById = (id: U | undefined | null) => id && this.nodesById.get(id);

  isDescendantOf = (nodeToTestId: U, ancestorId: U): boolean => {
    this.generateNodesByParentId();

    do {
      const node = this.getNodeById(nodeToTestId);
      if (!node) {
        return false;
      }
      const parentId = this.getParentId(node);
      if (parentId === ancestorId) {
        return true;
      }
      nodeToTestId = parentId;
    } while (nodeToTestId !== this.rootNodeId);

    return false;
  };

  getTree = () => {
    if (!this.cachedTree) {
      this.generateNodesByParentId();
      this.cachedTree = this.generateTree({
        dropCollapsedNodes: true,
        injectLoadMoreRow: true,
      });
    }
    return this.cachedTree;
  };

  getAllNodes = () => {
    if (!this.cachedAllNodes) {
      this.generateNodesByParentId();
      this.cachedAllNodes = this.generateTree({ dropCollapsedNodes: false });
    }
    return this.cachedAllNodes;
  };

  getDescendants = (nodeIds: U[]) => {
    const getDescendantIds = (nodeId: U) => {
      const childIds = (this.nodesByParentId
        .get(nodeId)
        ?.map((child) => child.id) ?? []) as U[];
      const descendantIds = childIds.flatMap(getDescendantIds) as U[];

      return [...childIds, ...descendantIds];
    };

    return nodeIds.flatMap(getDescendantIds).concat(nodeIds);
  };

  loadAllNodes = async () => {
    const loadUnloadedNodes = async (includeRoot = false): Promise<void> => {
      const unloadedNodes = Array.from(this.nodesById.values())
        .filter(
          (node) =>
            (!node.meta.hasLoaded && this.getHasChildren(node)) ||
            node.meta.hasMore,
        )
        .filter((node) => !!node.id && !node.meta.isLoadMoreNode)
        .map((node) => node.id);

      if (includeRoot) {
        unloadedNodes.push(this.rootNodeId as U);
      }

      if (unloadedNodes.length === 0) {
        return;
      }

      await Promise.all(
        unloadedNodes.map(async (nodeId) => {
          const { hasMore } =
            (await this.loadChildren(nodeId as U, {
              offset: 0,
              limit: MAX_NUM_CHILDREN,
            })) ?? {};

          //TODO: we can implement iterating through the pages but that can be deferred (it's not as simple as just
          //counting since we would have to track number of loaded nodes, which is easy, but potentially computationally
          // expensive.
          if (hasMore) {
            this.patchMeta(nodeId as U, { hasMore: true });
          }
        }),
      );

      await loadUnloadedNodes();
    };

    await this.batchChanges(() => loadUnloadedNodes(true));
  };

  flagDeletingNode = (nodeId: U) => {
    this.patchMeta(nodeId, { isDeleting: true });
  };

  optimisticallyMakeParent = (nodeId: U) => {
    const node = this.nodesById.get(nodeId);

    if (!node) {
      return;
    }

    this.nodesById.set(nodeId, this.setHasChildren(node, true));
    let currentNodeId = nodeId;

    // expand all ancestors
    return this.batchChanges(() => {
      let counter = 0;
      // out of abundance of caution, limit the loop
      while (
        currentNodeId !== this.rootNodeId &&
        counter++ < MAX_EXPAND_DEPTH
      ) {
        const currentNode = this.nodesById.get(currentNodeId);
        this.patchMeta(currentNodeId, { isExpanded: true });

        if (!currentNode) {
          break;
        }

        currentNodeId = this.getParentId(currentNode);
      }
    });
  };

  optimisticallyDeleteNode = (nodeId: U) => {
    return this.batchChanges(() => {
      this.generateNodesByParentId();

      const children = this.nodesByParentId.get(nodeId) ?? [];

      this.optimisticallyMoveNodes(
        children.map((child) => child.id as U),
        this.rootNodeId,
      );

      this.nodesById.delete(nodeId);
    });
  };

  optimisticallyMoveNodes = (nodeIds: U[], newParentId: U) => {
    return this.batchChanges(() => {
      nodeIds.forEach((nodeId) => {
        const node = this.nodesById.get(nodeId);
        const newParent = this.nodesById.get(newParentId);

        if (!node || (!newParent && newParentId !== this.rootNodeId)) {
          return;
        }

        if (this.isDescendantOf(newParentId, nodeId)) {
          return;
        }

        const oldParentId = this.getParentId(node);
        const oldParent = this.nodesById.get(oldParentId);

        this.generateNodesByParentId();

        this.nodesById.set(nodeId, this.setParentId(node, newParentId));

        if (this.nodesByParentId.get(oldParentId)?.length === 1 && oldParent) {
          this.nodesById.set(
            oldParentId,
            this.setHasChildren(oldParent, false),
          );
        }

        // Expand the new parent so the moved node is visible
        if (newParent) {
          this.nodesById.set(newParentId, {
            ...this.setHasChildren(newParent, true),
            meta: { ...newParent.meta, isExpanded: true },
          });
        }
      });
    });
  };
}
